import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, X, Trash2, Clipboard, Calendar, User as UserIcon, AlertTriangle, Link as LinkIcon, Bell, Check, ListChecks, MessageSquare } from 'lucide-react';

const LANES = [
  { id: 'todo',   label: 'Zu erledigen', color: 'border-gray-300',   head: 'bg-gray-50 text-gray-600' },
  { id: 'doing',  label: 'In Arbeit',    color: 'border-blue-300',   head: 'bg-blue-50 text-blue-700' },
  { id: 'review', label: 'Prüfung',      color: 'border-amber-300',  head: 'bg-amber-50 text-amber-700' },
  { id: 'done',   label: 'Erledigt',     color: 'border-green-300',  head: 'bg-green-50 text-green-700' },
];

const PRIO_META = {
  low:    { label: 'Niedrig',  color: 'text-gray-500 bg-gray-100' },
  medium: { label: 'Normal',   color: 'text-blue-600 bg-blue-50' },
  high:   { label: 'Dringend', color: 'text-red-600 bg-red-50' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE'); }
  catch { return iso; }
}

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (assigneeFilter) params.set('assignedTo', assigneeFilter);
    const res = await authFetch(`/api/tasks${params.toString() ? '?' + params : ''}`);
    if (res.ok) setTasks(await res.json());
  }, [search, assigneeFilter]);

  const loadUsers = useCallback(async () => {
    const res = await authFetch('/api/auth/users');
    if (res.ok) setUsers(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Deep-Link Support: ?task=<taskId> in URL → spezifische Aufgabe
  // direkt im Bearbeiten-Dialog öffnen. Wird vom LinkedTasksPanel
  // (Auftrag/Anfrage-Detail) genutzt — damit der User nicht in der
  // Aufgaben-Liste suchen muss. Holt die Aufgabe ohne `mine`-Filter,
  // damit sie auch dann auffindbar ist, wenn der angemeldete User
  // nicht zugewiesen ist.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get('task');
    if (!tid) return;
    let cancelled = false;
    (async () => {
      const r = await authFetch('/api/tasks?mine=false');
      if (!r.ok || cancelled) return;
      const all = await r.json();
      const t = all.find(x => x.id === tid);
      if (t) setEditing(t);
      const url = new URL(window.location.href);
      url.searchParams.delete('task');
      window.history.replaceState({}, '', url.toString());
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchTask = async (id, patch) => {
    await authFetch(`/api/tasks/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    load();
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return;
    await authFetch(`/api/tasks/${id}`, { method: 'DELETE' });
    load();
  };

  // Drag & drop via HTML5
  const onDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', task.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e, laneId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const t = tasks.find(x => x.id === id);
    if (t && t.status !== laneId) patchTask(id, { status: laneId });
  };

  return (
    <div className="p-6 h-full flex flex-col" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2">
            <Clipboard className="w-5 h-5 text-[#9ec043]" /> Aufgaben
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">Trello-Board für Support, Innendienst und Vertrieb</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-task">
          <Plus className="w-4 h-4 mr-1" /> Neue Aufgabe
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Suche nach Titel, Beschreibung, Herkunft…"
               className="flex-1 min-w-[240px] h-9 text-sm" data-testid="tasks-search" />
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="tasks-assignee-filter">
          <option value="">Alle Mitarbeiter</option>
          {users.map(u => <option key={u.email} value={u.email}>{u.name}</option>)}
        </select>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 min-h-0">
        {LANES.map(lane => {
          const laneTasks = tasks.filter(t => t.status === lane.id);
          return (
            <div key={lane.id}
                 onDragOver={onDragOver}
                 onDrop={(e) => onDrop(e, lane.id)}
                 className={`bg-white rounded-xl border-t-4 ${lane.color} shadow-sm flex flex-col min-h-[200px]`}
                 data-testid={`lane-${lane.id}`}>
              <div className={`px-3 py-2 rounded-t-lg ${lane.head} text-xs font-semibold flex items-center justify-between`}>
                <span>{lane.label}</span>
                <span className="text-[10px] opacity-70">{laneTasks.length}</span>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                {laneTasks.length === 0 && (
                  <p className="text-[11px] text-gray-400 text-center py-6">Keine Aufgaben</p>
                )}
                {laneTasks.map(task => (
                  <TaskCard key={task.id} task={task}
                            draggable
                            onDragStart={(e) => onDragStart(e, task)}
                            onEdit={() => setEditing(task)}
                            onDelete={() => deleteTask(task.id)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showNew && <TaskDialog users={users} currentUser={user} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <TaskDialog users={users} currentUser={user} task={editing} onDelete={deleteTask} onClose={() => { setEditing(null); load(); }} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TaskCard({ task, onEdit, onDelete, draggable, onDragStart, showStatus = false, showFollowups = false }) {
  const prio = PRIO_META[task.priority] || PRIO_META.medium;
  const { user: _me } = useAuth();
  const overdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';
  // Benachrichtigungs-Marker: zeigt für den AKTUELL EINGELOGGTEN User an,
  // ob es ungelesene Follow-up-Kommentare gibt (User-Spec 25.08.2026).
  // Frühere Logik nahm den Max über alle Teilnehmer → Bell blieb bei
  // Innendienst stehen auch wenn er längst gelesen hatte, weil z. B.
  // Produktion noch offen war. Neue Regel: jeder sieht seine eigene Bell.
  //
  // Escape-Konvention: E-Mails werden in `readCursors`-Keys mit `.`→`__DOT__`
  // encodiert, da MongoDB Punkte als Pfad-Trenner interpretiert.
  const _emailKey = (e) => (e || '').replace(/\./g, '__DOT__').replace(/\$/g, '__DLR__');
  const _readCursors = task.readCursors || {};
  const _legacyCreatorCursor = task.creatorLastReadAt
    ? new Date(task.creatorLastReadAt).getTime() : 0;
  const _creatorEmail = (task.createdBy || '').toLowerCase();
  const _myEmail = (_me?.email || '').toLowerCase();
  const _isAdmin = _me?.role === 'admin';
  const _participants = Array.from(new Set([
    _creatorEmail,
    (task.assignedTo || '').toLowerCase(),
    ...((task.assignees || []).map(a => (a || '').toLowerCase())),
  ].filter(Boolean)));
  // Bell nur wenn ich eingeloggt UND selbst Teilnehmer bin (Admin sieht die
  // Bell nur, wenn er auch tatsächlich Ersteller/Assignee ist — sonst wäre
  // die Bell für Admin bei jeder fremden Aufgabe störend).
  const _iAmParticipant = _myEmail && (
    _participants.includes(_myEmail) ||
    // Rollen-Bell: alle Mitglieder einer zugewiesenen Rolle sehen die Bell
    (_me?.role && (task._assigneeRoles || task.assigneeRoles || [])
      .map(r => (r || '').toLowerCase())
      .includes((_me.role || '').toLowerCase()))
  );
  const _cursorMs = (() => {
    if (!_myEmail) return 0;
    const raw = _readCursors[_emailKey(_myEmail)];
    const rc = raw ? new Date(raw).getTime() : 0;
    const legacy = (_myEmail === _creatorEmail) ? _legacyCreatorCursor : 0;
    return Math.max(rc, legacy);
  })();
  const unreadCount = (!_iAmParticipant && !_isAdmin)
    ? 0
    : (task.followups || []).filter(f =>
        f && new Date(f.createdAt).getTime() > _cursorMs &&
        (f.authorEmail || '').toLowerCase() !== _myEmail
      ).length;
  // „Neu für mich": frisch zugewiesene Aufgabe, die ich (als Bearbeiter oder
  // Rollen-Mitglied, nicht Ersteller) noch nicht geöffnet habe. assignedAt
  // rückt bei jeder (Neu-)Zuweisung vor; der Read-Cursor beim Öffnen.
  const _assignedAtMs = task.assignedAt ? new Date(task.assignedAt).getTime() : 0;
  const _isNewForMe = _iAmParticipant
    && _myEmail !== _creatorEmail
    && task.status !== 'done'
    && _assignedAtMs > 0
    && _cursorMs < _assignedAtMs;
  const _showAlert = unreadCount > 0 || _isNewForMe;
  return (
    <div draggable={draggable} onDragStart={onDragStart}
         className={`bg-gray-50 border rounded-lg p-2.5 cursor-move hover:border-[#9ec043] transition-colors ${
           task.status === 'done' && showStatus ? 'opacity-60 bg-green-50/40' : ''
         } ${_showAlert ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
         data-testid={`task-card-${task.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={onEdit} className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-1.5">
            {_isNewForMe && unreadCount === 0 && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold animate-pulse shrink-0"
                title="Neu dir zugewiesen — noch nicht geöffnet"
                data-testid={`task-new-${task.id}`}
              >
                <Bell className="w-2.5 h-2.5" /> Neu
              </span>
            )}
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold animate-pulse shrink-0"
                title={`${unreadCount} ungelesene${unreadCount === 1 ? 'r' : ''} Kommentar${unreadCount === 1 ? '' : 'e'}`}
                data-testid={`task-bell-${task.id}`}
              >
                <Bell className="w-2.5 h-2.5" /> {unreadCount}
              </span>
            )}
            <div className="text-xs font-medium text-[#32373c] line-clamp-2 flex-1">{task.title}</div>
          </div>
          {task.description && (
            <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{task.description}</div>
          )}
        </button>
        {task.dueDate && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              // Create a team-calendar event from the task
              const { authFetch } = await import('../../lib/auth-fetch');
              const start = new Date(task.dueDate);
              const end = new Date(start.getTime() + 60 * 60 * 1000);
              const r = await authFetch('/api/calendar/events', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: task.title,
                  description: task.description || '',
                  start: start.toISOString(),
                  end: end.toISOString(),
                  category: 'task',
                  attendees: task.assignees || [],
                }),
              });
              if (r.ok) { alert('Termin im Team-Kalender angelegt'); }
              else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Fehler'); }
            }}
            className="text-gray-300 hover:text-[#9ec043] shrink-0" title="Im Team-Kalender anlegen"
            data-testid={`btn-sync-task-${task.id}`}>
            <LinkIcon className="w-3 h-3" />
          </button>
        )}
        <button onClick={onDelete} className="text-gray-300 hover:text-red-500 shrink-0" data-testid={`btn-del-task-${task.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[10px]">
        <span className={`px-1.5 py-0.5 rounded ${prio.color}`}>{prio.label}</span>
        {showStatus && (
          <span
            className={`px-1.5 py-0.5 rounded ${
              task.status === 'done'
                ? 'bg-green-100 text-green-700'
                : task.status === 'doing'
                ? 'bg-blue-100 text-blue-700'
                : task.status === 'review'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600'
            }`}
            data-testid={`task-card-status-${task.id}`}
          >
            {task.status === 'done' ? '✓ Erledigt' : task.status === 'doing' ? 'In Arbeit' : task.status === 'review' ? 'Prüfung' : 'Offen'}
          </span>
        )}
        {(task.checklist || []).length > 0 && (
          (() => {
            const total = task.checklist.length;
            const done = task.checklist.filter(c => c.done).length;
            const all = total > 0 && done === total;
            return (
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${all ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                title="Checkliste"
                data-testid={`task-card-checklist-${task.id}`}
              >
                <ListChecks className="w-2.5 h-2.5" /> {done}/{total}
              </span>
            );
          })()
        )}
        {task.dueDate && (
          <span className={`inline-flex items-center gap-0.5 ${overdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
            {overdue && <AlertTriangle className="w-2.5 h-2.5" />}
            <Calendar className="w-2.5 h-2.5" /> {fmtDate(task.dueDate)}
          </span>
        )}
        {(task.assignees || []).slice(0, 3).map(a => (
          <span key={a} className="inline-flex items-center gap-0.5 text-gray-400">
            <UserIcon className="w-2.5 h-2.5" /> {a.split('@')[0]}
          </span>
        ))}
        {task.sourceLabel && (
          task.source === 'order' && task.sourceId ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Auftrag direkt öffnen — wir navigieren zur Aufträge-
                // Seite mit Query-Param `open=<orderId>`. Die Kanban-Seite
                // erkennt das beim Laden und öffnet den Auftrag.
                window.location.href = `/dashboard/auftraege?open=${encodeURIComponent(task.sourceId)}`;
              }}
              className="px-1.5 py-0.5 rounded bg-[#9ec043]/10 text-[#9ec043] hover:bg-[#9ec043]/20 truncate max-w-[180px] flex items-center gap-1"
              title={`Auftrag öffnen: ${task.sourceLabel}`}
              data-testid={`task-open-order-${task.id}`}
            >
              ← {task.sourceLabel}
            </button>
          ) : task.source === 'inquiry' && task.sourceId ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Anfrage direkt öffnen — Deep-Link auf die Anfragen/Angebote-
                // Seite (`?open=<inquiryId>`). InquiriesPage erkennt das,
                // setzt automatisch die richtige Kategorie und öffnet die
                // Anfrage im Detail-Panel.
                window.location.href = `/dashboard/angebote?open=${encodeURIComponent(task.sourceId)}`;
              }}
              className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 truncate max-w-[180px] flex items-center gap-1"
              title={`Anfrage öffnen: ${task.sourceLabel}`}
              data-testid={`task-open-inquiry-${task.id}`}
            >
              ← {task.sourceLabel}
            </button>
          ) : (
            <span className="px-1.5 py-0.5 rounded bg-[#9ec043]/10 text-[#9ec043] truncate max-w-[140px]" title={task.sourceLabel}>
              ← {task.sourceLabel}
            </span>
          )
        )}
      </div>
      {showFollowups && (task.followups || []).length > 0 && (
        (() => {
          // Follow-up-Preview direkt auf der Karte (Auftrag-Aufgaben-Tab).
          // Zeigt die letzten 3 Kommentare mit Autor & Datum, damit man
          // Verlauf sieht ohne die Aufgabe zu öffnen.
          const sorted = [...task.followups].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          const latest = sorted.slice(0, 3);
          const more = task.followups.length - latest.length;
          return (
            <div
              className="mt-2 border-t border-dashed border-gray-200 pt-2 space-y-1"
              data-testid={`task-card-followups-${task.id}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1">
                <MessageSquare className="w-2.5 h-2.5" /> Follow-up ({task.followups.length})
              </div>
              {latest.map(f => (
                <div key={f.id} className="text-[11px] leading-tight">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <span className="font-medium text-[#32373c]">{f.authorName || (f.authorEmail || '').split('@')[0]}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-[10px] text-gray-400">
                      {new Date(f.createdAt).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="text-gray-600 line-clamp-2 pl-0.5">
                    {f.comment}
                  </div>
                </div>
              ))}
              {more > 0 && (
                <div className="text-[10px] text-gray-400 italic">
                  … {more} weitere Kommentar{more === 1 ? '' : 'e'} — Aufgabe öffnen für Verlauf
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
}

function TaskDialog({ task, users, currentUser, onClose, onSaved, defaultSource, onDelete }) {
  const isEdit = !!task;
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [priority, setPriority] = useState(task?.priority || 'medium');
  const [status, setStatus] = useState(task?.status || 'todo');
  const [dueDate, setDueDate] = useState(task?.dueDate ? task.dueDate.slice(0, 10) : '');
  const [assignees, setAssignees] = useState(task?.assignees || []);
  const [assigneeRoles, setAssigneeRoles] = useState(task?.assigneeRoles || []);
  const [reminders, setReminders] = useState(task?.reminders || []);
  const [followups, setFollowups] = useState(task?.followups || []);
  const [checklist, setChecklist] = useState(task?.checklist || []);
  const [newChecklistText, setNewChecklistText] = useState('');
  const [addingChecklistItem, setAddingChecklistItem] = useState(false);
  const [creatorLastReadAt, setCreatorLastReadAt] = useState(task?.creatorLastReadAt || null);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [saving, setSaving] = useState(false);

  // Berechtigung „Als gelesen markieren": Ersteller, Assignee(s) oder Admin
  // (User-Erweiterung 15.07.26 — jeder Teilnehmer darf seinen eigenen
  // Read-Cursor setzen; Bell verschwindet erst wenn ALLE gelesen haben).
  const isAdmin = currentUser?.role === 'admin';
  const currentEmail = (currentUser?.email || '').toLowerCase();
  const isCreator = (task?.createdBy || '').toLowerCase() === currentEmail;
  const _dialogAssignees = [
    (task?.assignedTo || '').toLowerCase(),
    ...((task?.assignees || []).map(a => (a || '').toLowerCase())),
  ].filter(Boolean);
  const isAssignee = _dialogAssignees.includes(currentEmail);
  const canMarkRead = isEdit && (isCreator || isAssignee || isAdmin);

  // Cursor des AKTUELLEN Users — bevorzugt aus `readCursors[email]`,
  // Fallback auf `creatorLastReadAt` (nur wenn User = Creator ist).
  // Escape-Konvention wie in TaskCard: E-Mail-Key encodieren.
  const _emailKeyDialog = (e) => (e || '').replace(/\./g, '__DOT__').replace(/\$/g, '__DLR__');
  const _userCursorIso = (task?.readCursors || {})[_emailKeyDialog(currentEmail)]
    || (isCreator ? creatorLastReadAt : null);
  const cursorMs = _userCursorIso ? new Date(_userCursorIso).getTime() : 0;
  const unreadFollowups = (followups || []).filter(f =>
    f && new Date(f.createdAt).getTime() > cursorMs &&
    (f.authorEmail || '').toLowerCase() !== currentEmail
  );

  // „Neu für mich" (#10): frisch zugewiesene Aufgabe, die ich (als Bearbeiter
  // oder über meine Rolle) noch nicht geöffnet habe. Dieser Marker DARF beim
  // Öffnen verschwinden — anders als die Kommentar-Bell (siehe Spec unten).
  const _isRoleParticipant = (task?.assigneeRoles || []).includes(currentUser?.role);
  const _assignedAtMs = task?.assignedAt ? new Date(task.assignedAt).getTime() : 0;
  const isNewForMe = isEdit && !isCreator && (isAssignee || _isRoleParticipant)
    && task?.status !== 'done' && _assignedAtMs > 0 && cursorMs < _assignedAtMs;

  // ⚠️ 25.08.2026: Auto-Mark-Read beim Öffnen wurde ENTFERNT (User-Spec).
  // Die Bell soll NICHT mehr verschwinden, nur weil ein User die Aufgabe
  // öffnet. Sie verschwindet ausschließlich, wenn der User aktiv auf
  // „Als gelesen markieren" klickt oder der Auftrag/die Aufgabe auf
  // Status 'done' gesetzt wird (siehe _clear_bells_on_task_done).

  const markRead = async () => {
    if (!task?.id) return;
    const r = await authFetch(`/api/tasks/${task.id}/mark-followups-read`, { method: 'POST' });
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      if (d.creatorLastReadAt) setCreatorLastReadAt(d.creatorLastReadAt);
      // Optimistisch die lokale Anzeige auf 0 setzen — der User hat gerade
      // aktiv bestätigt, dass er alles gelesen hat.
      const now = new Date().toISOString();
      if (task && typeof task === 'object') {
        task.readCursors = { ...(task.readCursors || {}), [_emailKeyDialog(currentEmail)]: now };
      }
      // Signal an Parent zum Reload (der ruft onSaved-ähnlichen Reload).
      // TaskCard filtert eigene Cursors nach dem nächsten Load neu.
    }
  };

  // #10: Neu-Zuweisungs-Marker beim Öffnen quittieren — ABER nur, wenn es
  // keine ungelesenen Kommentare gibt (die unterliegen der expliziten
  // „Als gelesen markieren"-Spec vom 25.08.2026). So verschwindet der
  // „Neu"-Punkt für frisch zugewiesene Aufgaben, sobald der Bearbeiter sie
  // öffnet, ohne die Kommentar-Bell-Logik zu verletzen.
  useEffect(() => {
    if (isNewForMe && unreadFollowups.length === 0) {
      markRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAssignee = (email) => {
    setAssignees(prev => prev.includes(email) ? prev.filter(a => a !== email) : [...prev, email]);
  };

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    // Description ist im Backend immutable — wir schicken sie nur beim
    // Anlegen mit, beim PATCH (isEdit) wird sie ignoriert.
    const payload = isEdit ? {
      title, priority, status,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      assignees,
      assigneeRoles,
      reminders: reminders.filter(r => Number.isFinite(r) && r > 0),
    } : {
      title, description, priority, status,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      assignees,
      assigneeRoles,
      reminders: reminders.filter(r => Number.isFinite(r) && r > 0),
      // Erlaubt Aufrufern (OrderHistoryModal), neue Aufgaben direkt mit
      // Auftrag-Verknüpfung anzulegen. Wird beim Bearbeiten ignoriert.
      ...(defaultSource || {}),
    };
    const url = isEdit ? `/api/tasks/${task.id}` : '/api/tasks';
    const method = isEdit ? 'PATCH' : 'POST';
    const res = await authFetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.ok) onSaved();
  };

  const postComment = async () => {
    const c = newComment.trim();
    if (!c || !isEdit) return;
    setPostingComment(true);
    const res = await authFetch(`/api/tasks/${task.id}/followups`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: c }),
    });
    setPostingComment(false);
    if (res.ok) {
      const fu = await res.json();
      setFollowups(prev => [...prev, fu]);
      setNewComment('');
    }
  };

  const removeComment = async (fid) => {
    if (!window.confirm('Kommentar wirklich löschen?')) return;
    const res = await authFetch(`/api/tasks/${task.id}/followups/${fid}`, { method: 'DELETE' });
    if (res.ok) setFollowups(prev => prev.filter(f => f.id !== fid));
  };

  // ── Checklist-Handler ────────────────────────────────────────────────
  const addChecklistItem = async () => {
    const text = newChecklistText.trim();
    if (!text || !isEdit) return;
    setAddingChecklistItem(true);
    const res = await authFetch(`/api/tasks/${task.id}/checklist`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    setAddingChecklistItem(false);
    if (res.ok) {
      const item = await res.json();
      setChecklist(prev => [...prev, item]);
      setNewChecklistText('');
    }
  };

  const toggleChecklistItem = async (item) => {
    if (!isEdit) return;
    // Optimistisch updaten — bei Fehler rollback
    const next = !item.done;
    setChecklist(prev => prev.map(c => c.id === item.id ? { ...c, done: next } : c));
    const res = await authFetch(`/api/tasks/${task.id}/checklist/${item.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ done: next }),
    });
    if (res.ok) {
      const updated = await res.json();
      setChecklist(prev => prev.map(c => c.id === item.id ? updated : c));
    } else {
      // Rollback bei Fehler
      setChecklist(prev => prev.map(c => c.id === item.id ? { ...c, done: !next } : c));
    }
  };

  const removeChecklistItem = async (itemId) => {
    if (!window.confirm('Checklist-Punkt wirklich löschen?')) return;
    const res = await authFetch(`/api/tasks/${task.id}/checklist/${itemId}`, { method: 'DELETE' });
    if (res.ok) setChecklist(prev => prev.filter(c => c.id !== itemId));
  };

  // Sortierung: neueste Kommentare zuerst.
  const sortedFollowups = [...followups].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="task-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-[#32373c]">
            {isEdit ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-500">Titel</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
                   className="mt-1 h-9 text-sm" placeholder="z. B. Wechselrichter tauschen — Kunde Müller" data-testid="task-title" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">
              Beschreibung {isEdit && <span className="text-[10px] text-gray-400 ml-1">(Original-Kontext, nicht editierbar)</span>}
            </Label>
            <textarea
              value={description}
              onChange={e => !isEdit && setDescription(e.target.value)}
              rows={4}
              readOnly={isEdit}
              className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none ${isEdit ? 'bg-gray-50 text-gray-700 cursor-default' : 'bg-white focus:border-[#9ec043]'}`}
              data-testid="task-description"
            />
          </div>

          {isEdit && (
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50/50" data-testid="task-checklist">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                  <ListChecks className="w-3.5 h-3.5 text-[#9ec043]" />
                  Checkliste
                  {checklist.length > 0 && (
                    <span className="text-[10px] text-gray-400 ml-1">
                      {checklist.filter(c => c.done).length} / {checklist.length} erledigt
                    </span>
                  )}
                </Label>
              </div>
              {checklist.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {checklist.map(item => (
                    <li
                      key={item.id}
                      className={`flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1.5 text-sm ${item.done ? 'opacity-70' : ''}`}
                      data-testid={`task-checklist-item-${item.id}`}
                    >
                      <span className={`flex-1 break-words ${item.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {item.text}
                      </span>
                      {item.done && item.doneAt && (
                        <span className="text-[10px] text-gray-400">
                          {new Date(item.doneAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </span>
                      )}
                      <button
                        onClick={() => toggleChecklistItem(item)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          item.done
                            ? 'bg-[#9ec043] border-[#9ec043] text-white'
                            : 'bg-white border-gray-300 hover:border-[#9ec043]'
                        }`}
                        data-testid={`task-checklist-toggle-${item.id}`}
                        title={item.done ? 'Hat-Aktion zurücknehmen' : 'Als erledigt markieren'}
                      >
                        {item.done && <Check className="w-3 h-3" strokeWidth={3} />}
                      </button>
                      <button
                        onClick={() => removeChecklistItem(item.id)}
                        className="text-gray-300 hover:text-red-500"
                        title="Checklist-Punkt löschen"
                        data-testid={`task-checklist-del-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <Input
                  value={newChecklistText}
                  onChange={e => setNewChecklistText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                  placeholder="Neuen Punkt hinzufügen…"
                  className="flex-1 h-8 text-sm bg-white"
                  data-testid="task-checklist-input"
                />
                <Button
                  onClick={addChecklistItem}
                  disabled={!newChecklistText.trim() || addingChecklistItem}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-8 px-3"
                  data-testid="task-checklist-add"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              {checklist.length === 0 && (
                <p className="text-[11px] text-gray-400 italic mt-1.5">
                  Noch keine Checkpunkte. Mit Enter oder + hinzufügen.
                </p>
              )}
            </div>
          )}

          {isEdit && (
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50/50" data-testid="task-followups">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-gray-500">
                  Follow-up-Kommentare ({followups.length})
                  {unreadFollowups.length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-semibold" data-testid="task-followups-unread-badge">
                      <Bell className="w-2.5 h-2.5" /> {unreadFollowups.length} neu
                    </span>
                  )}
                </Label>
                {canMarkRead && unreadFollowups.length > 0 && (
                  <button
                    onClick={markRead}
                    className="text-[11px] text-[#9ec043] hover:underline"
                    data-testid="task-followups-mark-read"
                  >
                    Als gelesen markieren
                  </button>
                )}
              </div>
              <div className="flex gap-2 mb-3">
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  rows={2}
                  placeholder="Kommentar hinzufügen — z. B. Status-Update, Rückfrage, Fortschritt…"
                  className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#9ec043]"
                  data-testid="task-followup-input"
                />
                <Button
                  onClick={postComment}
                  disabled={!newComment.trim() || postingComment}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-auto px-3 self-start"
                  data-testid="task-followup-submit"
                >
                  {postingComment ? '…' : 'Hinzufügen'}
                </Button>
              </div>
              {sortedFollowups.length === 0 ? (
                <p className="text-[11px] text-gray-400 italic">Noch keine Kommentare. Neueste werden oben angezeigt.</p>
              ) : (
                <ul className="space-y-2 max-h-[280px] overflow-y-auto">
                  {sortedFollowups.map(f => (
                    <li key={f.id} className="bg-white border border-gray-200 rounded p-2 text-sm" data-testid={`task-followup-${f.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px]">
                          <span className="font-medium text-[#32373c]">{f.authorName}</span>
                          <span className="text-gray-400 ml-2">
                            {new Date(f.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <button
                          onClick={() => removeComment(f.id)}
                          className="text-gray-300 hover:text-red-500"
                          title="Kommentar löschen"
                          data-testid={`task-followup-del-${f.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-xs text-gray-700 whitespace-pre-wrap break-words">{f.comment}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Spalte</Label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="task-status">
                {LANES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Priorität</Label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="task-priority">
                <option value="low">Niedrig</option>
                <option value="medium">Normal</option>
                <option value="high">Dringend</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Fälligkeit</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                     className="mt-1 h-9 text-sm" data-testid="task-due" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Zugewiesene Mitarbeiter</Label>
            <div className="flex flex-wrap gap-1.5">
              {users.map(u => {
                const active = assignees.includes(u.email);
                return (
                  <button key={u.email} onClick={() => toggleAssignee(u.email)}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-[#9ec043] text-white border-[#9ec043]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#9ec043]'}`}
                          data-testid={`task-assignee-${u.email}`}>
                    {u.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Rollen-Zuweisung — nur bei GLOBALEN Aufgaben. Bei Auftrags-
              Aufgaben (defaultSource.source='order') kaskadiert die Rolle
              der ausgewählten Personen automatisch (siehe TaskCard).
              User-Spec 25.08.2026: 1a für globale Aufgaben. */}
          {(!defaultSource || defaultSource.source !== 'order') && (
            <div data-testid="task-assignee-roles">
              <Label className="text-xs text-gray-500 mb-1 block">Zugewiesene Rollen (ganze Teams)</Label>
              <div className="flex flex-wrap gap-1.5">
                {['admin', 'innendienst', 'vertrieb', 'technik', 'support', 'produktion', 'team'].map(r => {
                  const active = assigneeRoles.includes(r);
                  return (
                    <button key={r} type="button" onClick={() => {
                      setAssigneeRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
                    }}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'}`}
                            data-testid={`task-role-${r}`}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                Alle Mitglieder der ausgewählten Rollen erhalten die Bell für diese Aufgabe.
              </div>
            </div>
          )}
          {task?.sourceLabel && (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
              Verknüpft mit: <span className="font-medium text-[#9ec043]">{task.sourceLabel}</span>
            </div>
          )}

          {/* Reminder-Konfiguration: bis zu 2 Erinnerungs-Offsets analog Kalender */}
          <div data-testid="task-reminders-section">
            <Label className="text-xs text-gray-500 flex items-center gap-1">
              <span>🔔</span> Erinnerungen (max. 2)
            </Label>
            <div className="mt-1 space-y-1.5">
              {([0, 1]).map(slotIdx => {
                const cur = reminders[slotIdx];
                return (
                  <div key={slotIdx} className="flex items-center gap-2">
                    <select
                      value={cur ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        setReminders(prev => {
                          const next = [...prev];
                          while (next.length <= slotIdx) next.push(null);
                          next[slotIdx] = v;
                          return next.filter(x => x != null);
                        });
                      }}
                      className="flex-1 h-8 text-xs border border-gray-200 rounded px-2 bg-white"
                      data-testid={`task-reminder-${slotIdx}`}
                    >
                      <option value="">— Keine Erinnerung —</option>
                      <option value="5">5 Minuten vorher</option>
                      <option value="15">15 Minuten vorher</option>
                      <option value="30">30 Minuten vorher</option>
                      <option value="60">1 Stunde vorher</option>
                      <option value="120">2 Stunden vorher</option>
                      <option value="240">4 Stunden vorher</option>
                      <option value="1440">1 Tag vorher</option>
                      <option value="2880">2 Tage vorher</option>
                      <option value="10080">1 Woche vorher</option>
                    </select>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              {dueDate
                ? 'Versandt per Mail an alle zugewiesenen Mitarbeiter + den Ersteller — relativ zur Fälligkeit.'
                : 'Hinweis: Erinnerungen werden nur versendet, wenn ein Fälligkeitsdatum gesetzt ist.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-between items-center">
          <div className="flex gap-2">
            {isEdit && (
              <>
                <Button
                  variant="outline"
                  onClick={async () => {
                    // Direkt-Aktion: Aufgabe als erledigt markieren und
                    // schließen — löst gleichzeitig Bell-Clearing im
                    // Backend aus (siehe _clear_bells_on_task_done).
                    setSaving(true);
                    const r = await authFetch(`/api/tasks/${task.id}`, {
                      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: 'done' }),
                    });
                    setSaving(false);
                    if (r.ok) onSaved();
                  }}
                  disabled={saving || status === 'done'}
                  className="h-9 text-sm border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="btn-task-mark-done"
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  {status === 'done' ? 'Bereits erledigt' : 'Als erledigt'}
                </Button>
                {onDelete && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      // Parent-Handler zeigt bereits window.confirm und
                      // gibt true/false zurück. Nur bei true schließen.
                      const ok = await onDelete(task.id);
                      if (ok) onClose();
                    }}
                    className="h-9 text-sm border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="btn-task-delete-dialog"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 text-sm">Abbrechen</Button>
            <Button onClick={save} disabled={saving || !title.trim()}
                    className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-save-task">
              {saving ? 'Speichert…' : 'Speichern'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Named Exports für Wiederverwendung (z. B. OrderHistoryModal-Tasks-Tab).
export { TaskCard, TaskDialog };
